import { useState, useEffect, useRef } from 'react';
import { fetchCached } from '@/lib/api';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import SectionWatermark from '@/components/SectionWatermark';
import { FALLBACK_FAITH_CONTENT } from '@/lib/publicPageData';

function useInView(ref) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return inView;
}

function AnimatedSection({ children, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref);
  return (
    <div ref={ref}
      style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(24px)', transition: `all 0.7s ease-out ${delay}ms` }}>
      {children}
    </div>
  );
}

export default function StatementOfFaithPage() {
  const [content, setContent] = useState(FALLBACK_FAITH_CONTENT);

  useEffect(() => {
    fetchCached('/content/faith').then(setContent).catch(() => {});
  }, []);

  return (
    <div data-testid="faith-page" className="pt-20">
      {/* Hero */}
      <section className="mscm-hero-panel mscm-cinematic-hero py-20 md:py-28">
        <div className="mscm-hero-aurora" />
        <div className="mscm-star-field" />
        <div className="mscm-luminous-sweep" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <span className="text-white/85 text-xs font-bold uppercase tracking-[0.16em]">Million Stars Community Ministry</span>
          <h1 className="mscm-hero-title mt-4 font-bold text-white lg:text-6xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {content.title}
          </h1>
          <p className="mt-4 text-white/80 text-base md:text-lg max-w-2xl mx-auto">
            {content.intro}
          </p>
        </div>
      </section>

      {/* Faith Items */}
      <SectionWatermark
        className="py-24 md:py-32 bg-white"
        contentClassName="max-w-3xl mx-auto px-6 md:px-8"
        opacity={0.11}
      >
          <Accordion type="single" collapsible defaultValue="item-1" data-testid="faith-accordion">
            {(content.items || []).map((item, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <AccordionItem value={`item-${item.number}`} className="border-b border-[#E2E8F0]">
                  <AccordionTrigger
                    data-testid={`faith-item-trigger-${item.number}`}
                    className="py-6 text-left hover:no-underline"
                  >
                    <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                      <span className="flex-shrink-0 w-10 h-10 bg-[#1E4FA1] text-white rounded-xl flex items-center justify-center font-bold text-sm">
                        {item.number}
                      </span>
                      <span className="mscm-text-safe min-w-0 text-lg md:text-xl font-bold text-[#0F172A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                        {item.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pl-0 sm:pl-14 pb-6">
                    <p className="text-[#475569] leading-relaxed">{item.text}</p>
                  </AccordionContent>
                </AccordionItem>
              </AnimatedSection>
            ))}
          </Accordion>
      </SectionWatermark>
    </div>
  );
}
